import React, { useState, useMemo } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  Paper,
  Switch,
  Box,
} from "@mantine/core";
import { FieldSchema, ValidationDict } from "../../../types/staticWebGenerator";
import { isFieldMissing, parseSchemaFields } from "../utils/validation";
import {
  IconChevronLeft,
  IconPhoto,
  IconMessageCircle,
  IconChevronRight,
  IconBell,
  IconRectangle,
  IconSparkles,
  IconUsersGroup,
} from "@tabler/icons-react";
import WebsiteGeneratorGallery from "./WebsiteGeneratorGallery";
import WebsiteGeneratorTestimonials from "./WebsiteGeneratorTestimonials";
import WebsiteGeneratorAnnouncementBanner from "./WebsiteGeneratorAnnouncementBanner";
import WebsiteGeneratorBanner from "./WebsiteGeneratorBanner";
import WebsiteGeneratorHighlights from "./WebsiteGeneratorHighlights";
import WebsiteGeneratorPhotoGallery from "./WebsiteGeneratorPhotoGallery";
import WebsiteGeneratorTeamContent from "./WebsiteGeneratorTeamContent";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_BACKGROUND_WHITE = "#ffffff";

interface WebsiteGeneratorOptionalSectionsContentProps {
  onBack: () => void;
  galleryContent:
    | any[]
    | { items?: any[]; title?: string; gridLayout?: string };
  onGalleryContentChange: (
    items: any[] | { items?: any[]; title?: string; gridLayout?: string },
  ) => void;
  testimonialsContent: any;
  onTestimonialsContentChange?: (content: any) => void;
  announcementBannerContent?: any;
  onAnnouncementBannerContentChange?: (content: any) => void;
  bannerContent?: any;
  onBannerContentChange?: (content: any) => void;
  highlightsContent?: any;
  onHighlightsContentChange?: (content: any) => void;
  teamContent?: any;
  onTeamContentChange?: (content: any) => void;
  optionalSectionStates: any;
  onOptionalSectionStatesChange: (s: any) => void;
  missingFields?: string[];
  fieldSchema?: FieldSchema | null;
  validationDict?: ValidationDict;
}

type SectionKey =
  | "gallery"
  | "testimonials"
  | "announcementBanner"
  | "banner"
  | "highlights"
  | "team";

const WebsiteGeneratorOptionalSectionsContent: React.FC<
  WebsiteGeneratorOptionalSectionsContentProps
> = ({
  onBack,
  galleryContent,
  onGalleryContentChange,
  testimonialsContent,
  onTestimonialsContentChange,
  announcementBannerContent,
  onAnnouncementBannerContentChange,
  bannerContent,
  onBannerContentChange,
  highlightsContent,
  onHighlightsContentChange,
  teamContent,
  onTeamContentChange,
  optionalSectionStates,
  onOptionalSectionStatesChange,
  missingFields = [],
  fieldSchema,
  validationDict = {},
}) => {
  const [selectedSection, setSelectedSection] = useState<SectionKey | null>(
    null,
  );
  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);

  const handleSectionToggle = (sectionKey: SectionKey) => {
    if (onOptionalSectionStatesChange) {
      onOptionalSectionStatesChange({
        ...optionalSectionStates,
        [sectionKey]: !optionalSectionStates?.[sectionKey],
      });
    }
  };

  const handleSectionSelect = (section: SectionKey) => {
    setSelectedSection(section);
  };

  const handleBackToSections = () => {
    setSelectedSection(null);
  };

  // Calculate dynamic counts based on content
  const galleryCount = Array.isArray(galleryContent)
    ? galleryContent.length
    : galleryContent?.items?.length || 0;
  const testimonialsCount = testimonialsContent?.cards?.length || 0;
  const announcementBannerCount = announcementBannerContent?.body ? 1 : 0;
  const bannerCount = bannerContent?.text ? 1 : 0;
  const highlightsCount = highlightsContent?.items?.length || 0;
  const teamCount = teamContent?.title ? 1 : 0;

  // Standard optional sections that always show
  const STANDARD_OPTIONAL_SECTIONS: Array<{
    sectionKey: SectionKey;
    schemaId: string | null;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    getCount: () => string;
  }> = [
    {
      sectionKey: "gallery",
      schemaId: "gallery",
      title: "Gallery",
      subtitle: "Photo gallery",
      icon: <IconPhoto size={20} />,
      getCount: () => `${galleryCount}/10`,
    },
    {
      sectionKey: "testimonials",
      schemaId: "testimonials",
      title: "Testimonials",
      subtitle: "Customer testimonials",
      icon: <IconMessageCircle size={20} />,
      getCount: () => `${testimonialsCount}/5`,
    },
    {
      sectionKey: "announcementBanner",
      schemaId: "announcement_modal", // Maps to announcement_modal in schema
      title: "Announcement Banner",
      subtitle: "Announcement banner",
      icon: <IconBell size={20} />,
      getCount: () => `${announcementBannerCount}/1`,
    },
    {
      sectionKey: "banner",
      schemaId: "banner", // Maps to banner in schema
      title: "Banner",
      subtitle: "Banner section",
      icon: <IconRectangle size={20} />,
      getCount: () => `${bannerCount}/1`,
    },
    {
      sectionKey: "highlights",
      schemaId: "service_highlights", // Maps to service_highlights in schema
      title: "Highlights",
      subtitle: "Highlight items",
      icon: <IconSparkles size={20} />,
      getCount: () => `${highlightsCount}/4`,
    },
    {
      sectionKey: "team",
      schemaId: "team", // Maps to team in schema
      title: "Team",
      subtitle: "Team section",
      icon: <IconUsersGroup size={20} />,
      getCount: () => `${teamCount}/1`,
    },
  ];

  // Check if a section has missing required fields (only if enabled)
  // For optional sections: only check required fields if the section is enabled (toggle ON)
  const sectionHasMissingFields = (
    schemaId: string | null,
    sectionKey: SectionKey,
  ): boolean => {
    if (!schemaId || !fieldSchema) {
      // Even without schemaId, check validationDict directly
      const isEnabled = optionalSectionStates?.[sectionKey];
      if (isEnabled) {
        const sectionValidation = validationDict[sectionKey];
        if (sectionValidation && Object.keys(sectionValidation).length > 0) {
          const hasError = Object.values(sectionValidation).some(
            (v) => v === true,
          );
          if (hasError) {
            return true;
          }
        }
      }
      return false;
    }

    const isEnabled = optionalSectionStates?.[sectionKey];
    if (!isEnabled) {
      return false; // Only check if enabled
    }

    // Check validationDict for this section - if it has any errors, return true
    const sectionValidation = validationDict[sectionKey];
    if (sectionValidation && Object.keys(sectionValidation).length > 0) {
      const hasError = Object.values(sectionValidation).some((v) => v === true);
      if (hasError) {
        return true;
      }
    }

    // Check missingFields for ANY paths starting with this schemaId (including "required if provided" fields)
    // This catches fields like "banner.content" and "announcement_modal.body" even if not marked as required in schema
    const hasMissingFieldsForSchema = missingFields.some((fieldPath) => {
      return fieldPath.startsWith(schemaId + ".");
    });

    if (hasMissingFieldsForSchema) {
      return true;
    }

    // Also check missingFields for this section's schema paths from schema
    const fields = parseSchemaFields(fieldSchema);
    const sectionRequiredFields = fields.filter((field) => {
      return field.required && field.path.startsWith(schemaId + ".");
    });

    // Check if any of these required fields are in the missingFields array
    const hasMissing = sectionRequiredFields.some((field) => {
      return missingFields.includes(field.path);
    });

    // Also check if the section itself is missing (top-level)
    const isSectionMissing = missingFields.includes(schemaId);

    return hasMissing || isSectionMissing;
  };

  // Build options array: always show all 6 standard sections
  const optionalSectionsOptions = STANDARD_OPTIONAL_SECTIONS.map((section) => {
    const enabled = optionalSectionStates?.[section.sectionKey] || false;
    const hasMissingFields = section.schemaId
      ? sectionHasMissingFields(section.schemaId, section.sectionKey)
      : false;

    return {
      title: section.title,
      subtitle: section.subtitle,
      icon: section.icon,
      count: section.getCount(),
      enabled,
      onClick: () => handleSectionSelect(section.sectionKey),
      onToggle: () => handleSectionToggle(section.sectionKey),
      sectionKey: section.sectionKey,
      hasError: hasMissingFields,
    };
  });

  // Render specific section content
  if (selectedSection === "gallery") {
    // Handle both array and object formats for galleryContent
    const galleryItems = Array.isArray(galleryContent)
      ? galleryContent
      : galleryContent?.items || [];
    const galleryObject = Array.isArray(galleryContent)
      ? { items: galleryContent }
      : galleryContent || { items: [] };

    // Map galleryItems to ImageData format for the modal
    const galleryImages = galleryItems.map((item) => ({
      id: item.id || item.filename,
      url: item.url || item.image_url || item.src,
      src: item.url || item.image_url || item.src,
      filename: item.filename || item.title,
      selected: item.selected || false,
    }));

    return (
      <>
        <WebsiteGeneratorGallery
          onBack={handleBackToSections}
          images={galleryItems as any[]}
          selectedImages={(galleryItems as any[]).filter(
            (p: any) => p.selected,
          )}
          missingFields={missingFields}
          requiredValidation={validationDict.gallery || {}}
          galleryContent={galleryObject}
          onGalleryContentChange={(content) => {
            // Update gallery content - preserve structure but allow title updates
            if (onGalleryContentChange) {
              // Always pass the full content object now to support title field
              onGalleryContentChange(content);
            }
          }}
          onImageSelect={(photo) => {
            const updated = (galleryItems as any[]).map((p: any) =>
              p.id === photo.id ? { ...p, selected: !p.selected } : p,
            );
            // Preserve full gallery object structure (title, gridLayout)
            onGalleryContentChange({
              ...galleryObject,
              items: updated,
            });
          }}
          onImageDelete={(photo) => {
            const updated = (galleryItems as any[]).filter(
              (p: any) => p.id !== photo.id,
            );
            // Preserve full gallery object structure (title, gridLayout)
            onGalleryContentChange({
              ...galleryObject,
              items: updated,
            });
          }}
          onSeeMore={() => setIsPhotoGalleryOpen(true)}
        />

        {/* Photo Gallery Modal */}
        <WebsiteGeneratorPhotoGallery
          isOpen={isPhotoGalleryOpen}
          onClose={() => setIsPhotoGalleryOpen(false)}
          images={[]}
          selectedImages={galleryImages.filter((img) => img.selected)}
          onImageSelect={(selectedImages) => {
            // Limit to 10 items max
            const limitedImages = selectedImages.slice(0, 10);

            // Map selected images to gallery content format
            // Preserve existing title/description from galleryContent if available
            const existingItemsForLookup = Array.isArray(galleryContent)
              ? galleryContent
              : galleryContent?.items || [];
            const newGalleryContent = limitedImages.map((img) => {
              // Find existing item in galleryContent to preserve metadata
              const existingItem = (existingItemsForLookup as any[]).find(
                (item: any) =>
                  item.id === img.id || item.filename === img.filename,
              );

              return {
                id: img.id || img.filename,
                url: img.url || img.src,
                image_url: img.url || img.src,
                filename: img.filename,
                title: existingItem?.title || img.filename,
                description: existingItem?.description || "",
                selected: true,
              };
            });

            // Merge with existing gallery content, keeping only selected ones
            const existingItems = Array.isArray(galleryContent)
              ? galleryContent
              : galleryContent?.items || [];
            const existingSelected = (existingItems as any[]).filter(
              (p: any) => p.selected,
            );
            const merged = [...existingSelected, ...newGalleryContent]
              .slice(0, 10) // Ensure max 10 items
              .map((item, idx) => ({ ...item, id: item.id || idx + 1 }));

            // Preserve gallery title and gridLayout when updating items
            const galleryObject = Array.isArray(galleryContent)
              ? { items: galleryContent }
              : galleryContent || {};
            onGalleryContentChange({ ...galleryObject, items: merged });
          }}
          onImageDelete={(image) => {
            const existingItems = Array.isArray(galleryContent)
              ? galleryContent
              : galleryContent?.items || [];
            const updated = (existingItems as any[]).filter(
              (p: any) => p.id !== image.id && p.filename !== image.filename,
            );
            // Preserve gallery title and gridLayout when updating items
            const galleryObject = Array.isArray(galleryContent)
              ? { items: galleryContent }
              : galleryContent || {};
            onGalleryContentChange({ ...galleryObject, items: updated });
          }}
          onConfirmSelection={() => {
            // Selection is handled by onImageSelect
          }}
        />
      </>
    );
  }

  if (selectedSection === "testimonials") {
    if (!onTestimonialsContentChange) return null;
    return (
      <WebsiteGeneratorTestimonials
        onBack={handleBackToSections}
        testimonialsContent={testimonialsContent}
        onContentChange={onTestimonialsContentChange}
        missingFields={missingFields}
        requiredValidation={validationDict.testimonials || {}}
      />
    );
  }

  if (selectedSection === "announcementBanner") {
    if (!onAnnouncementBannerContentChange) return null;
    const announcementBannerValidation =
      validationDict.announcementBanner || {};
    return (
      <WebsiteGeneratorAnnouncementBanner
        announcementBannerContent={announcementBannerContent}
        onContentChange={onAnnouncementBannerContentChange}
        onBack={handleBackToSections}
        missingFields={missingFields}
        requiredValidation={announcementBannerValidation}
      />
    );
  }

  if (selectedSection === "banner") {
    if (!onBannerContentChange) return null;
    const bannerValidation = validationDict.banner || {};
    return (
      <WebsiteGeneratorBanner
        bannerContent={bannerContent}
        onContentChange={onBannerContentChange}
        onBack={handleBackToSections}
        missingFields={missingFields}
        requiredValidation={bannerValidation}
      />
    );
  }

  if (selectedSection === "highlights") {
    if (!onHighlightsContentChange) return null;
    // Get validation for highlights section
    const highlightsValidation = validationDict.highlights || {};
    return (
      <WebsiteGeneratorHighlights
        highlightsContent={highlightsContent}
        onContentChange={onHighlightsContentChange}
        onBack={handleBackToSections}
        requiredValidation={highlightsValidation}
        missingFields={missingFields}
      />
    );
  }

  if (selectedSection === "team") {
    if (!onTeamContentChange) return null;
    const teamValidation = validationDict.team || {};
    return (
      <WebsiteGeneratorTeamContent
        teamContent={teamContent}
        onContentChange={onTeamContentChange}
        onBack={handleBackToSections}
        missingFields={missingFields}
        requiredValidation={teamValidation}
      />
    );
  }

  // Render sections list: always show all 6 standard optional sections
  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Optional Sections
        </Text>
      </Group>

      <Stack gap="sm">
        {optionalSectionsOptions.map((option, index) => {
          const handlePaperClick = (evt: React.MouseEvent) => {
            // Check if the click came from the toggle switch or its container
            const clickedElement = evt.target as HTMLElement;
            const toggleSwitch =
              clickedElement.closest(".toggle-switch-container") ||
              clickedElement.closest(".toggle-switch") ||
              clickedElement.closest(".mantine-Switch-root");

            if (toggleSwitch) {
              // User clicked the toggle - prevent navigation
              evt.stopPropagation();
              return;
            }

            // User clicked elsewhere on the card - navigate to section
            if (option.enabled && option.onClick) {
              option.onClick();
            }
          };

          return (
            <Paper
              key={index}
              p="md"
              style={{
                border: `1px solid ${option.hasError ? "red" : COLOR_BORDER_LIGHT}`,
                borderRadius: "8px",
                cursor: option.enabled ? "pointer" : "not-allowed",
                opacity: option.enabled ? 1 : 0.6,
                transition: "all 0.2s ease",
              }}
              onClick={handlePaperClick}
              onMouseEnter={(e) => {
                if (option.enabled) {
                  e.currentTarget.style.backgroundColor =
                    COLOR_BACKGROUND_LIGHT;
                }
              }}
              onMouseLeave={(e) => {
                if (option.enabled) {
                  e.currentTarget.style.backgroundColor =
                    COLOR_BACKGROUND_WHITE;
                }
              }}
            >
              <Group justify="space-between" align="center">
                <Group gap="sm">
                  {/* Toggle Switch */}
                  <Box
                    className="toggle-switch-container"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Switch
                      checked={option.enabled}
                      className="toggle-switch"
                      onChange={() => {
                        // When switch is toggled, update the state
                        if (option.onToggle) {
                          option.onToggle();
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      size="sm"
                      color="blue"
                    />
                  </Box>

                  {/* Icon */}
                  <ActionIcon
                    variant="light"
                    color={option.enabled ? "blue" : "gray"}
                    size="lg"
                    radius="md"
                  >
                    {option.icon}
                  </ActionIcon>

                  <Box>
                    <Text
                      fw={500}
                      size="sm"
                      c={
                        option.enabled
                          ? option.hasError
                            ? "red"
                            : undefined
                          : "dimmed"
                      }
                    >
                      {option.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {option.subtitle}
                    </Text>
                  </Box>
                </Group>

                <Group gap="sm" align="center">
                  <Text size="xs" c="dimmed">
                    {option.count}
                  </Text>
                  {option.enabled && (
                    <ActionIcon variant="subtle" color="gray">
                      <IconChevronRight size={16} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default WebsiteGeneratorOptionalSectionsContent;
